import { upperFirst } from 'lodash';
import { constants } from '@clubmed/utils';
import type { FavoriteProductType } from 'Context/FavoriteProductListContext';
import 'Context/FavoriteProductListContext';
import { timestampToApiDate } from 'Utils/date/apiDate';
const { BOOKING_STATUSES, TRAVELPLAN_TYPES } = constants;
type Options = {
  status: BookingStatus;
};

const mapStayBooking = (favorite: FavoriteProductType, { status }: Options) => ({
  attendees: [],
  categories: {
    isOption: false,
    isOptionRenewed: false,
    isExpiredOption: false,
    isStandard: false,
    isTravelPlan: true,
    isTravelPlanPrebooking: favorite.type === TRAVELPLAN_TYPES.TRAVELPLAN_TYPE_PRE_BOOKING,
    isTravelPlanStandard: favorite.type !== TRAVELPLAN_TYPES.TRAVELPLAN_TYPE_PRE_BOOKING,
  },
  creationDate: '',
  dates: {
    duration: favorite.duration,
    endDate: timestampToApiDate(favorite.arrivalDate) || '',
    startDate: timestampToApiDate(favorite.departureDate) || '',
  },
  id: favorite.id || favorite.reference || '',
  isMultiSaleContracts: false,
  isMultiStays: false,
  isShoppingSearch: status === BOOKING_STATUSES.BOOKING_STATUS_BOOKMARK,
  membership: null,
  optionDurability: null,
  packages: [],
  payment: {
    due: {
      deadlineDate: '',
      formattedValue: favorite.priceOfStay?.bestPrice?.formattedValue || '',
      value: favorite.priceOfStay?.bestPrice?.value || 0,
    },
    isEnabled: false,
    paid: null,
    status: {
      isInCredit: false,
      isPaid: false,
      isPending: false,
      isUnPaid: true,
      name: '',
    },
  },
  status: {
    name: status,
    isArchived: false,
    isCanceled: false,
    isExpiredHope: false,
    isExpiredOption: false,
    isExpiredQuotation: false,
    isHope: false,
    isOption: false,
    isQuotation: false,
    isRequest: false,
    isSearch: true,
    isValidated: false,
  },
  totalPrice: {
    formattedValue: '',
    value: 0,
  },
  vendor: {
    isSoldByClubmed: true,
    isSoldByPartners: false,
    name: '',
    phoneNumber: '',
    soldBy: '',
    type: '',
  },
});

const mapBooking = (favorite: FavoriteProductType, { status }: Options) => ({
  ...mapStayBooking(favorite, {
    status,
  }),
  stays: [
    {
      accommodation: {
        rooms: [],
        stock: 0,
        streetViews: [],
      },
      additionalServices: {
        activities: [],
        childcares: [],
        insurance: [],
        rentals: [],
        transfers: [],
      },
      booking: {
        ...mapStayBooking(favorite, {
          status,
        }),
      },
      dates: {
        duration: favorite.duration,
        endDate: timestampToApiDate(favorite.arrivalDate) || '',
        isCurrent: false,
        isPast: false,
        isToCome: true,
        startDate: timestampToApiDate(favorite.departureDate) || '',
      },
      features: {
        easyArrival: {
          status: {
            isActivated: false,
            isAvailable: false,
            isDone: false,
            isNotYet: false,
            isUnavailable: false,
            isUneditable: false,
            isValidated: false,
            name: '',
          },
          url: '',
        },
        easyCheckInOut: {
          easyCheckIn: {
            isActivated: false,
            status: {
              isActivated: false,
              isAvailable: false,
              isDone: false,
              isNotYet: false,
              isUnavailable: false,
              isUneditable: false,
              isValidated: false,
              name: '',
            },
          },
          easyCheckOut: {
            isActivated: false,
            status: {
              isActivated: false,
              isAvailable: false,
              isDone: false,
              isNotYet: false,
              isUnavailable: false,
              isUneditable: false,
              isValidated: false,
              name: '',
            },
          },
        },
        easyTransfer: {
          serviceStatus: {
            isUpsellAvailable: false,
            isValidated: false,
            name: '',
          },
          status: {
            isActivated: false,
            isAvailable: false,
            isDone: false,
            isNotYet: false,
            isUnavailable: false,
            isUneditable: false,
            isValidated: false,
            name: '',
          },
        },
        policyForm: {
          status: {
            isAvailable: false,
          },
        },
        feedback: {
          status: {
            isAvailable: false,
            name: '',
          },
          url: '',
        },
        pickYourRoom: {
          status: {
            isActivated: false,
            isResortOnly: false,
            isValidated: false,
            name: '',
          },
        },
      },
      id: '',
      isBookable: favorite.isBookable,
      participants: {
        adultsCount: favorite.adultsNumber,
        attendees: [...favorite.kids],
        children: favorite.kids,
        childrenCount: favorite.childrenCount || (favorite.kids || []).length,
        totalCount:
          favorite.adultsNumber + (favorite.childrenCount || (favorite.kids || []).length || 0),
      },
      product: favorite.product,
      staff: [],
      stayIndex: 0,
      transfers: [],
      transport:
        favorite.departureCity && favorite.departureCity !== 'NO'
          ? {
              fromCity: upperFirst(favorite.departureCity.toLowerCase()),
              fromCityCode: favorite.departureCity,
              inbounds: [],
              outbounds: [],
              toCity: '',
              toCityCode: '',
              type: '',
              types: [],
            }
          : null,
      upsells: {
        activities: [],
        childcares: [],
        insurance: [],
        packages: [],
        rentals: [],
        transfers: favorite.upsells?.transfers || [],
        whiteLabelServices: [],
      },
      vouchers: [],
    },
  ],
});

export const bookmarkToBooking = (favorite: FavoriteProductType) =>
  mapBooking(favorite, {
    status: BOOKING_STATUSES.BOOKING_STATUS_BOOKMARK,
  });
export const searchToBooking = (favorite: FavoriteProductType) =>
  mapBooking(favorite, {
    status: BOOKING_STATUSES.BOOKING_STATUS_SEARCH,
  });
